import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";
import { Backdrop, Title, Kicker } from "../components";
import { COLORS, FONT, NEUTRAL, SAFE } from "../theme";

type Stage = { label: string; reqs?: string[] };
const STAGES: Stage[] = [
  { label: "Puerto" },
  { label: "PDI / Aduana", reqs: ["4 fotos", "checklist"] },
  { label: "Bodega", reqs: ["4 fotos"] },
  { label: "Dealers", reqs: ["4 fotos"] },
  { label: "Facturación" },
  { label: "Entrega", reqs: ["4 fotos", "firma"] },
];

const TRACK_X0 = 210;
const TRACK_X1 = 1710;
const TRACK_Y = 560;
const nodeX = (i: number) =>
  TRACK_X0 + ((TRACK_X1 - TRACK_X0) * i) / (STAGES.length - 1);

const CarMini: React.FC = () => (
  <svg width="66" height="66" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 11l1.5-4A2 2 0 0 1 8.4 5.7h7.2a2 2 0 0 1 1.9 1.3L19 11m-14 0h14m-14 0a2 2 0 0 0-2 2v3h2m14-5a2 2 0 0 1 2 2v3h-2M6 16h12M7 16v1.5M17 16v1.5"
      stroke={COLORS.white}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="8" cy="16.4" r="1.5" fill={COLORS.white} />
    <circle cx="16" cy="16.4" r="1.5" fill={COLORS.white} />
  </svg>
);

export const S4Process: React.FC = () => {
  const frame = useCurrentFrame();

  // Progreso del auto a lo largo de la línea (arranca tras el título).
  const start = 34;
  const prog = interpolate(frame, [start, start + 120], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.4, 0, 0.2, 1),
  });
  const carX = TRACK_X0 + (TRACK_X1 - TRACK_X0) * prog;
  const fillW = carX - TRACK_X0;

  return (
    <AbsoluteFill>
      <Backdrop tint="mix" />
      <AbsoluteFill style={{ padding: SAFE, paddingTop: SAFE * 0.7 }}>
        <div style={{ marginBottom: 16 }}>
          <Kicker>El proceso</Kicker>
        </div>
        <Title size={80} maxWidth={1500}>
          Un solo flujo, de principio a fin
        </Title>
      </AbsoluteFill>

      {/* Línea base */}
      <div
        style={{
          position: "absolute",
          left: TRACK_X0,
          top: TRACK_Y,
          width: TRACK_X1 - TRACK_X0,
          height: 6,
          borderRadius: 6,
          background: NEUTRAL.lineStrong,
        }}
      />
      {/* Línea de progreso */}
      <div
        style={{
          position: "absolute",
          left: TRACK_X0,
          top: TRACK_Y,
          width: fillW,
          height: 6,
          borderRadius: 6,
          background: `linear-gradient(90deg, ${COLORS.blue}, ${COLORS.purple})`,
        }}
      />

      {/* Nodos */}
      {STAGES.map((s, i) => {
        const x = nodeX(i);
        const reached = carX >= x - 4;
        const appear = interpolate(frame, [8 + i * 6, 22 + i * 6], [0, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const pop = interpolate(frame, [8 + i * 6, 24 + i * 6], [0.6, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
          easing: Easing.bezier(0.16, 1, 0.3, 1),
        });
        const accent = reached ? COLORS.blueLight : NEUTRAL.lineStrong;
        return (
          <div key={i} style={{ opacity: appear }}>
            {/* punto */}
            <div
              style={{
                position: "absolute",
                left: x - 15,
                top: TRACK_Y - 12,
                width: 30,
                height: 30,
                borderRadius: 30,
                background: reached ? COLORS.blueLight : COLORS.panel,
                border: `3px solid ${accent}`,
                boxShadow: reached
                  ? `0 0 24px ${COLORS.blueLight}88`
                  : "none",
                scale: String(pop),
              }}
            />
            {/* etiqueta */}
            <div
              style={{
                position: "absolute",
                left: x - 120,
                top: TRACK_Y + 42,
                width: 240,
                textAlign: "center",
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: 30,
                color: reached ? COLORS.white : COLORS.sub,
              }}
            >
              {s.label}
            </div>
            {/* chips de requisito */}
            {s.reqs && (
              <div
                style={{
                  position: "absolute",
                  left: x - 120,
                  top: TRACK_Y + 92,
                  width: 240,
                  display: "flex",
                  gap: 8,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {s.reqs.map((r, ri) => {
                  const chipOp = interpolate(
                    frame,
                    [reachFrame(i) + ri * 5, reachFrame(i) + 14 + ri * 5],
                    [0, 1],
                    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                  );
                  const isFirma = r === "firma";
                  const c = isFirma ? COLORS.greenLight : COLORS.purpleLight;
                  return (
                    <span
                      key={ri}
                      style={{
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: 22,
                        color: c,
                        background: `${c}1e`,
                        border: `1px solid ${c}55`,
                        borderRadius: 20,
                        padding: "5px 14px",
                        opacity: chipOp,
                      }}
                    >
                      {r}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Auto en movimiento */}
      <div
        style={{
          position: "absolute",
          left: carX - 33,
          top: TRACK_Y - 78,
          width: 66,
          height: 66,
          filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.5))",
        }}
      >
        <CarMini />
      </div>
    </AbsoluteFill>
  );
};

// Cuándo el auto alcanza el nodo i (para sincronizar chips).
function reachFrame(i: number) {
  const start = 34;
  const dur = 120;
  const t = i / (STAGES.length - 1);
  return Math.round(start + dur * t);
}
